import { useEffect } from 'react';
import { useSiteConfig } from './useSiteConfig';
import { SiteTheme } from '../utils/theme';

/**
 * TypeScript interfaces for the data-driven CSS system
 */
interface SiteColors {
  // Hero Section
  heroTitle: string;
  heroSubtitle: string;
  heroDescription: string;
  championshipText: string;

  // Navigation
  navLinkHover: string;
  navLinkActive: string;
  navLinkActiveBorder: string;

  // Buttons
  ctaPrimary: string;
  ctaSecondary: string;
  ctaHoverShadow: string;
  secondaryButtonBorder: string;
  secondaryButtonText: string;
  secondaryButtonHoverBg: string;
  secondaryButtonHoverText: string;
  exploreChampionshipsBorder: string;
  exploreChampionshipsText: string;
  exploreChampionshipsHoverBg: string;
  exploreChampionshipsHoverShadow: string;
  startNowBorder: string;
  startNowText: string;
  startNowHoverBg: string;
  startNowHoverShadow: string;

  // Text Classes
  primaryText: string;
  secondaryText: string;
  accentText: string;

  // Gradient Text
  gradientStart: string;
  gradientEnd: string;

  // Cards
  championshipCardBorder: string;
  championshipCardBg: string;
  championshipCardHoverBorder: string;
  championshipCardHoverShadow: string;
  teamCardBorder: string;
  teamCardHoverBorder: string;
  teamName: string;
  teamStats: string;
  teamRecord: string;
  glassCardBorder: string;
  glassCardBg: string;
  glassCardHoverBorder: string;
  glassCardHoverBg: string;

  // Badges
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;

  // Stats
  statValue: string;
  statLabel: string;
  metricValue: string;

  // Tables
  tableHeaderBg: string;
  tableHeaderText: string;
  tableHeaderBorder: string;
  tableRowHoverBg: string;

  // Forms
  inputFocusBorder: string;
  inputFocusShadow: string;

  // Loading
  loadingSpinnerBorder: string;
  loadingSpinnerTop: string;

  // Links
  linkHover: string;

  // Neon Effect
  neonText: string;
  neonShadow: string;

  // Sport-Specific
  sportElementBorder: string;
  sportElementText: string;

  // CSS Variables
  cssVarPrimaryText: string;
  cssVarSecondaryText: string;
  cssVarAccentText: string;
  cssVarBgPrimary: string;
  cssVarBorderPrimary: string;
}

/**
 * Color path mapping interface
 */
interface ColorMapping {
  [key: string]: string; // e.g., "primary.500", "secondary.200", etc.
}

/**
 * CSS Rule interface for data-driven CSS generation
 */
interface CSSRule {
  selector: string;
  properties: Record<string, string | ((colors: SiteColors) => string)>;
}

/**
 * Site-specific style configurations
 * Maps each site to its preferred color assignments
 */
const SITE_STYLE_CONFIGS: Record<string, ColorMapping> = {
  'The Football Machine': {
    // Hero Section - NFL uses Gold as primary, Red as secondary
    heroTitle: 'secondary.500',
    heroSubtitle: 'primary.500',
    heroDescription: 'accent.50',
    championshipText: 'secondary.500',

    // Navigation
    navLinkHover: 'secondary.500',
    navLinkActive: 'primary.500',
    navLinkActiveBorder: 'primary.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'secondary.500',
    ctaHoverShadow: 'secondary.500',
    secondaryButtonBorder: 'secondary.500',
    secondaryButtonText: 'secondary.500',
    secondaryButtonHoverBg: 'secondary.500',
    secondaryButtonHoverText: 'primary.900',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'secondary.500',
    startNowBorder: 'secondary.500',
    startNowText: 'secondary.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'secondary.500',

    // Text Classes
    primaryText: 'secondary.500',
    secondaryText: 'primary.500',
    accentText: 'accent.50',

    // Gradient Text
    gradientStart: 'secondary.500',
    gradientEnd: 'primary.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'secondary.500',
    championshipCardHoverShadow: 'secondary.500',
    teamCardBorder: 'accent.50',
    teamCardHoverBorder: 'secondary.500',
    teamName: 'secondary.500',
    teamStats: 'primary.500',
    teamRecord: 'accent.50',
    glassCardBorder: 'secondary.500',
    glassCardBg: 'secondary.500',
    glassCardHoverBorder: 'secondary.500',
    glassCardHoverBg: 'secondary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'secondary.500',
    statLabel: 'accent.50',
    metricValue: 'primary.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'secondary.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'secondary.500',

    // Forms
    inputFocusBorder: 'secondary.500',
    inputFocusShadow: 'secondary.500',

    // Loading
    loadingSpinnerBorder: 'secondary.500',
    loadingSpinnerTop: 'secondary.500',

    // Links
    linkHover: 'secondary.500',

    // Neon Effect
    neonText: 'secondary.500',
    neonShadow: 'secondary.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'secondary.500',

    // CSS Variables
    cssVarPrimaryText: 'secondary.500',
    cssVarSecondaryText: 'primary.500',
    cssVarAccentText: 'accent.50',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The Basketball Machine': {
    // Hero Section - NBA uses primary brown tones
    heroTitle: 'primary.500',
    heroSubtitle: 'secondary.500',
    heroDescription: 'quaternary.500',
    championshipText: 'primary.500',

    // Navigation
    navLinkHover: 'quinary.500',
    navLinkActive: 'accent.500',
    navLinkActiveBorder: 'accent.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'quinary.500',
    ctaHoverShadow: 'quinary.500',
    secondaryButtonBorder: 'quinary.500',
    secondaryButtonText: 'quinary.500',
    secondaryButtonHoverBg: 'quinary.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'quinary.500',
    startNowBorder: 'quinary.500',
    startNowText: 'quinary.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'quinary.500',

    // Text Classes
    primaryText: 'primary.500',
    secondaryText: 'accent.500',
    accentText: 'quinary.500',

    // Gradient Text
    gradientStart: 'primary.500',
    gradientEnd: 'quinary.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'quinary.500',
    championshipCardHoverShadow: 'quinary.500',
    teamCardBorder: 'secondary.500',
    teamCardHoverBorder: 'quinary.500',
    teamName: 'primary.500',
    teamStats: 'accent.500',
    teamRecord: 'quinary.500',
    glassCardBorder: 'primary.500',
    glassCardBg: 'primary.500',
    glassCardHoverBorder: 'quinary.500',
    glassCardHoverBg: 'primary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'primary.500',
    statLabel: 'secondary.500',
    metricValue: 'quinary.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'quinary.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'quinary.500',

    // Forms
    inputFocusBorder: 'quinary.500',
    inputFocusShadow: 'quinary.500',

    // Loading
    loadingSpinnerBorder: 'quinary.500',
    loadingSpinnerTop: 'quinary.500',

    // Links
    linkHover: 'quinary.500',

    // Neon Effect
    neonText: 'quinary.500',
    neonShadow: 'quinary.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'quinary.500',

    // CSS Variables
    cssVarPrimaryText: 'primary.500',
    cssVarSecondaryText: 'accent.500',
    cssVarAccentText: 'quinary.500',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The Baseball Machine': {
    // Hero Section - MLB uses blue tones
    heroTitle: 'primary.500',
    heroSubtitle: 'secondary.500',
    heroDescription: 'accent.500',
    championshipText: 'primary.500',

    // Navigation
    navLinkHover: 'accent.500',
    navLinkActive: 'secondary.500',
    navLinkActiveBorder: 'secondary.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'secondary.500',
    ctaHoverShadow: 'accent.500',
    secondaryButtonBorder: 'accent.500',
    secondaryButtonText: 'accent.500',
    secondaryButtonHoverBg: 'accent.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'accent.500',
    startNowBorder: 'accent.500',
    startNowText: 'accent.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'accent.500',

    // Text Classes
    primaryText: 'primary.500',
    secondaryText: 'secondary.500',
    accentText: 'accent.500',

    // Gradient Text
    gradientStart: 'primary.500',
    gradientEnd: 'accent.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'accent.500',
    championshipCardHoverShadow: 'accent.500',
    teamCardBorder: 'secondary.500',
    teamCardHoverBorder: 'accent.500',
    teamName: 'primary.500',
    teamStats: 'secondary.500',
    teamRecord: 'accent.500',
    glassCardBorder: 'primary.500',
    glassCardBg: 'primary.500',
    glassCardHoverBorder: 'accent.500',
    glassCardHoverBg: 'primary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'primary.500',
    statLabel: 'secondary.500',
    metricValue: 'accent.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'accent.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'accent.500',

    // Forms
    inputFocusBorder: 'accent.500',
    inputFocusShadow: 'accent.500',

    // Loading
    loadingSpinnerBorder: 'accent.500',
    loadingSpinnerTop: 'accent.500',

    // Links
    linkHover: 'accent.500',

    // Neon Effect
    neonText: 'accent.500',
    neonShadow: 'accent.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'accent.500',

    // CSS Variables
    cssVarPrimaryText: 'primary.500',
    cssVarSecondaryText: 'secondary.500',
    cssVarAccentText: 'accent.500',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The Hockey Machine': {
    // Hero Section - NHL uses grayscale with selective color
    heroTitle: 'quaternary.500',
    heroSubtitle: 'quinary.500',
    heroDescription: 'secondary.500',
    championshipText: 'quaternary.500',

    // Navigation
    navLinkHover: 'quinary.500',
    navLinkActive: 'accent.500',
    navLinkActiveBorder: 'accent.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'quinary.500',
    ctaHoverShadow: 'quinary.500',
    secondaryButtonBorder: 'quinary.500',
    secondaryButtonText: 'quinary.500',
    secondaryButtonHoverBg: 'quinary.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'quinary.500',
    startNowBorder: 'quinary.500',
    startNowText: 'quinary.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'quinary.500',

    // Text Classes
    primaryText: 'quaternary.500',
    secondaryText: 'quinary.500',
    accentText: 'accent.500',

    // Gradient Text
    gradientStart: 'quaternary.500',
    gradientEnd: 'quinary.500',

    // Cards
    championshipCardBorder: 'accent.500',
    championshipCardBg: 'accent.500',
    championshipCardHoverBorder: 'quinary.500',
    championshipCardHoverShadow: 'quinary.500',
    teamCardBorder: 'quaternary.500',
    teamCardHoverBorder: 'quinary.500',
    teamName: 'quaternary.500',
    teamStats: 'quinary.500',
    teamRecord: 'accent.500',
    glassCardBorder: 'accent.500',
    glassCardBg: 'accent.500',
    glassCardHoverBorder: 'quinary.500',
    glassCardHoverBg: 'accent.500',

    // Badges
    badgeBg: 'accent.500',
    badgeText: 'accent.500',
    badgeBorder: 'accent.500',

    // Stats
    statValue: 'quaternary.500',
    statLabel: 'quinary.500',
    metricValue: 'accent.500',

    // Tables
    tableHeaderBg: 'accent.500',
    tableHeaderText: 'quaternary.500',
    tableHeaderBorder: 'accent.500',
    tableRowHoverBg: 'quinary.500',

    // Forms
    inputFocusBorder: 'quinary.500',
    inputFocusShadow: 'quinary.500',

    // Loading
    loadingSpinnerBorder: 'quinary.500',
    loadingSpinnerTop: 'quinary.500',

    // Links
    linkHover: 'quinary.500',

    // Neon Effect
    neonText: 'quinary.500',
    neonShadow: 'quinary.500',

    // Sport-Specific
    sportElementBorder: 'accent.500',
    sportElementText: 'quaternary.500',

    // CSS Variables
    cssVarPrimaryText: 'quaternary.500',
    cssVarSecondaryText: 'quinary.500',
    cssVarAccentText: 'accent.500',
    cssVarBgPrimary: 'accent.500',
    cssVarBorderPrimary: 'accent.500',
  },

  'The College Football Machine': {
    // Hero Section - CFB uses maroon and teal
    heroTitle: 'primary.500',
    heroSubtitle: 'quinary.500',
    heroDescription: 'secondary.500',
    championshipText: 'primary.500',

    // Navigation
    navLinkHover: 'quinary.500',
    navLinkActive: 'accent.500',
    navLinkActiveBorder: 'accent.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'quinary.500',
    ctaHoverShadow: 'quinary.500',
    secondaryButtonBorder: 'quinary.500',
    secondaryButtonText: 'quinary.500',
    secondaryButtonHoverBg: 'quinary.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'quinary.500',
    startNowBorder: 'quinary.500',
    startNowText: 'quinary.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'quinary.500',

    // Text Classes
    primaryText: 'primary.500',
    secondaryText: 'quinary.500',
    accentText: 'accent.500',

    // Gradient Text
    gradientStart: 'primary.500',
    gradientEnd: 'quinary.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'quinary.500',
    championshipCardHoverShadow: 'quinary.500',
    teamCardBorder: 'secondary.500',
    teamCardHoverBorder: 'quinary.500',
    teamName: 'primary.500',
    teamStats: 'accent.500',
    teamRecord: 'quinary.500',
    glassCardBorder: 'primary.500',
    glassCardBg: 'primary.500',
    glassCardHoverBorder: 'quinary.500',
    glassCardHoverBg: 'primary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'primary.500',
    statLabel: 'secondary.500',
    metricValue: 'quinary.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'quinary.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'quinary.500',

    // Forms
    inputFocusBorder: 'quinary.500',
    inputFocusShadow: 'quinary.500',

    // Loading
    loadingSpinnerBorder: 'quinary.500',
    loadingSpinnerTop: 'quinary.500',

    // Links
    linkHover: 'quinary.500',

    // Neon Effect
    neonText: 'quinary.500',
    neonShadow: 'quinary.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'quinary.500',

    // CSS Variables
    cssVarPrimaryText: 'primary.500',
    cssVarSecondaryText: 'quinary.500',
    cssVarAccentText: 'accent.500',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The College Basketball Machine': {
    // Hero Section - CBB uses navy and light blue
    heroTitle: 'primary.500',
    heroSubtitle: 'secondary.500',
    heroDescription: 'quaternary.500',
    championshipText: 'primary.500',

    // Navigation
    navLinkHover: 'accent.500',
    navLinkActive: 'quinary.500',
    navLinkActiveBorder: 'quinary.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'accent.500',
    ctaHoverShadow: 'accent.500',
    secondaryButtonBorder: 'accent.500',
    secondaryButtonText: 'accent.500',
    secondaryButtonHoverBg: 'accent.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'accent.500',
    startNowBorder: 'accent.500',
    startNowText: 'accent.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'accent.500',

    // Text Classes
    primaryText: 'primary.500',
    secondaryText: 'accent.500',
    accentText: 'quinary.500',

    // Gradient Text
    gradientStart: 'primary.500',
    gradientEnd: 'accent.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'accent.500',
    championshipCardHoverShadow: 'accent.500',
    teamCardBorder: 'secondary.500',
    teamCardHoverBorder: 'accent.500',
    teamName: 'primary.500',
    teamStats: 'quaternary.500',
    teamRecord: 'accent.500',
    glassCardBorder: 'primary.500',
    glassCardBg: 'primary.500',
    glassCardHoverBorder: 'accent.500',
    glassCardHoverBg: 'primary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'primary.500',
    statLabel: 'secondary.500',
    metricValue: 'accent.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'accent.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'accent.500',

    // Forms
    inputFocusBorder: 'accent.500',
    inputFocusShadow: 'accent.500',

    // Loading
    loadingSpinnerBorder: 'accent.500',
    loadingSpinnerTop: 'accent.500',

    // Links
    linkHover: 'accent.500',

    // Neon Effect
    neonText: 'accent.500',
    neonShadow: 'accent.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'accent.500',

    // CSS Variables
    cssVarPrimaryText: 'primary.500',
    cssVarSecondaryText: 'accent.500',
    cssVarAccentText: 'quinary.500',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The College Baseball Machine': {
    // Hero Section - CBASE uses dark green tones
    heroTitle: 'primary.500',
    heroSubtitle: 'quinary.500',
    heroDescription: 'secondary.500',
    championshipText: 'primary.500',

    // Navigation
    navLinkHover: 'quinary.500',
    navLinkActive: 'accent.500',
    navLinkActiveBorder: 'accent.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'quinary.500',
    ctaHoverShadow: 'quinary.500',
    secondaryButtonBorder: 'quinary.500',
    secondaryButtonText: 'quinary.500',
    secondaryButtonHoverBg: 'quinary.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'quinary.500',
    startNowBorder: 'quinary.500',
    startNowText: 'quinary.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'quinary.500',

    // Text Classes
    primaryText: 'primary.500',
    secondaryText: 'quinary.500',
    accentText: 'accent.500',

    // Gradient Text
    gradientStart: 'primary.500',
    gradientEnd: 'quinary.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'quinary.500',
    championshipCardHoverShadow: 'quinary.500',
    teamCardBorder: 'secondary.500',
    teamCardHoverBorder: 'quinary.500',
    teamName: 'primary.500',
    teamStats: 'accent.500',
    teamRecord: 'quinary.500',
    glassCardBorder: 'primary.500',
    glassCardBg: 'primary.500',
    glassCardHoverBorder: 'quinary.500',
    glassCardHoverBg: 'primary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'primary.500',
    statLabel: 'secondary.500',
    metricValue: 'quinary.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'quinary.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'quinary.500',

    // Forms
    inputFocusBorder: 'quinary.500',
    inputFocusShadow: 'quinary.500',

    // Loading
    loadingSpinnerBorder: 'quinary.500',
    loadingSpinnerTop: 'quinary.500',

    // Links
    linkHover: 'quinary.500',

    // Neon Effect
    neonText: 'quinary.500',
    neonShadow: 'quinary.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'quinary.500',

    // CSS Variables
    cssVarPrimaryText: 'primary.500',
    cssVarSecondaryText: 'quinary.500',
    cssVarAccentText: 'accent.500',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The College Hockey Machine': {
    // Hero Section - CHOCK uses grayscale with blue accents
    heroTitle: 'primary.500',
    heroSubtitle: 'quinary.500',
    heroDescription: 'secondary.500',
    championshipText: 'primary.500',

    // Navigation
    navLinkHover: 'quinary.500',
    navLinkActive: 'accent.500',
    navLinkActiveBorder: 'accent.500',

    // Buttons
    ctaPrimary: 'primary.500',
    ctaSecondary: 'quinary.500',
    ctaHoverShadow: 'quinary.500',
    secondaryButtonBorder: 'quinary.500',
    secondaryButtonText: 'quinary.500',
    secondaryButtonHoverBg: 'quinary.500',
    secondaryButtonHoverText: 'primary.50',
    exploreChampionshipsBorder: 'primary.500',
    exploreChampionshipsText: 'primary.500',
    exploreChampionshipsHoverBg: 'primary.500',
    exploreChampionshipsHoverShadow: 'quinary.500',
    startNowBorder: 'quinary.500',
    startNowText: 'quinary.500',
    startNowHoverBg: 'primary.500',
    startNowHoverShadow: 'quinary.500',

    // Text Classes
    primaryText: 'primary.500',
    secondaryText: 'quinary.500',
    accentText: 'accent.500',

    // Gradient Text
    gradientStart: 'primary.500',
    gradientEnd: 'quinary.500',

    // Cards
    championshipCardBorder: 'primary.500',
    championshipCardBg: 'primary.500',
    championshipCardHoverBorder: 'quinary.500',
    championshipCardHoverShadow: 'quinary.500',
    teamCardBorder: 'quaternary.500',
    teamCardHoverBorder: 'quinary.500',
    teamName: 'primary.500',
    teamStats: 'accent.500',
    teamRecord: 'quinary.500',
    glassCardBorder: 'primary.500',
    glassCardBg: 'primary.500',
    glassCardHoverBorder: 'quinary.500',
    glassCardHoverBg: 'primary.500',

    // Badges
    badgeBg: 'primary.500',
    badgeText: 'primary.500',
    badgeBorder: 'primary.500',

    // Stats
    statValue: 'primary.500',
    statLabel: 'quaternary.500',
    metricValue: 'quinary.500',

    // Tables
    tableHeaderBg: 'primary.500',
    tableHeaderText: 'quinary.500',
    tableHeaderBorder: 'primary.500',
    tableRowHoverBg: 'quinary.500',

    // Forms
    inputFocusBorder: 'quinary.500',
    inputFocusShadow: 'quinary.500',

    // Loading
    loadingSpinnerBorder: 'quinary.500',
    loadingSpinnerTop: 'quinary.500',

    // Links
    linkHover: 'quinary.500',

    // Neon Effect
    neonText: 'quinary.500',
    neonShadow: 'quinary.500',

    // Sport-Specific
    sportElementBorder: 'primary.500',
    sportElementText: 'quinary.500',

    // CSS Variables
    cssVarPrimaryText: 'primary.500',
    cssVarSecondaryText: 'quinary.500',
    cssVarAccentText: 'accent.500',
    cssVarBgPrimary: 'primary.500',
    cssVarBorderPrimary: 'primary.500',
  },

  'The Sports Machine': {
    // Hero Section - TSM uses teal and green tones
    heroTitle: 'accent.500',
    heroSubtitle: 'secondary.500',
    heroDescription: 'primary.500',
    championshipText: 'accent.500',

    // Navigation
    navLinkHover: 'quaternary.500',
    navLinkActive: 'quinary.500',
    navLinkActiveBorder: 'quinary.500',

    // Buttons
    ctaPrimary: 'accent.500',
    ctaSecondary: 'quaternary.500',
    ctaHoverShadow: 'quaternary.500',
    secondaryButtonBorder: 'quaternary.500',
    secondaryButtonText: 'quaternary.500',
    secondaryButtonHoverBg: 'quaternary.500',
    secondaryButtonHoverText: 'primary.950',
    exploreChampionshipsBorder: 'accent.500',
    exploreChampionshipsText: 'accent.500',
    exploreChampionshipsHoverBg: 'accent.500',
    exploreChampionshipsHoverShadow: 'quaternary.500',
    startNowBorder: 'quaternary.500',
    startNowText: 'quaternary.500',
    startNowHoverBg: 'accent.500',
    startNowHoverShadow: 'quaternary.500',

    // Text Classes
    primaryText: 'accent.500',
    secondaryText: 'quaternary.500',
    accentText: 'quinary.500',

    // Gradient Text
    gradientStart: 'accent.500',
    gradientEnd: 'quaternary.500',

    // Cards
    championshipCardBorder: 'accent.500',
    championshipCardBg: 'accent.500',
    championshipCardHoverBorder: 'quaternary.500',
    championshipCardHoverShadow: 'quaternary.500',
    teamCardBorder: 'secondary.500',
    teamCardHoverBorder: 'quaternary.500',
    teamName: 'accent.500',
    teamStats: 'primary.500',
    teamRecord: 'quaternary.500',
    glassCardBorder: 'accent.500',
    glassCardBg: 'accent.500',
    glassCardHoverBorder: 'quaternary.500',
    glassCardHoverBg: 'accent.500',

    // Badges
    badgeBg: 'accent.500',
    badgeText: 'accent.500',
    badgeBorder: 'accent.500',

    // Stats
    statValue: 'accent.500',
    statLabel: 'secondary.500',
    metricValue: 'quaternary.500',

    // Tables
    tableHeaderBg: 'accent.500',
    tableHeaderText: 'quaternary.500',
    tableHeaderBorder: 'accent.500',
    tableRowHoverBg: 'quaternary.500',

    // Forms
    inputFocusBorder: 'quaternary.500',
    inputFocusShadow: 'quaternary.500',

    // Loading
    loadingSpinnerBorder: 'quaternary.500',
    loadingSpinnerTop: 'quaternary.500',

    // Links
    linkHover: 'quaternary.500',

    // Neon Effect
    neonText: 'quaternary.500',
    neonShadow: 'quaternary.500',

    // Sport-Specific
    sportElementBorder: 'accent.500',
    sportElementText: 'quaternary.500',

    // CSS Variables
    cssVarPrimaryText: 'accent.500',
    cssVarSecondaryText: 'quaternary.500',
    cssVarAccentText: 'quinary.500',
    cssVarBgPrimary: 'accent.500',
    cssVarBorderPrimary: 'accent.500',
  },
};

/**
 * CSS rules template - defines all the CSS rules that will be generated
 */
const CSS_RULES: CSSRule[] = [
  // CSS Variables
  {
    selector: ':root',
    properties: {
      '--site-primary-text': (colors) => colors.cssVarPrimaryText,
      '--site-secondary-text': (colors) => colors.cssVarSecondaryText,
      '--site-accent-text': (colors) => colors.cssVarAccentText,
      '--site-bg-primary': (colors) => `${colors.cssVarBgPrimary}15`,
      '--site-border-primary': (colors) => `${colors.cssVarBorderPrimary}50`,
    },
  },

  // Hero Styles
  {
    selector: '.hero-title',
    properties: {
      color: (colors) => `${colors.heroTitle} !important`,
      'text-shadow': (colors) => `0 0 20px ${colors.heroTitle}80`,
    },
  },
  {
    selector: '.hero-subtitle',
    properties: {
      color: (colors) => `${colors.heroSubtitle} !important`,
    },
  },
  {
    selector: '.hero-description',
    properties: {
      color: (colors) => `${colors.heroDescription} !important`,
    },
  },
  {
    selector: '.championship-text, .super-bowl-text',
    properties: {
      color: (colors) => `${colors.championshipText} !important`,
      'text-shadow': (colors) => `0 0 30px ${colors.championshipText}80`,
    },
  },

  // Navigation
  {
    selector: '.nav-link:hover',
    properties: {
      color: (colors) => `${colors.navLinkHover} !important`,
    },
  },
  {
    selector: '.nav-link.active',
    properties: {
      color: (colors) => `${colors.navLinkActive} !important`,
      'border-bottom-color': (colors) => `${colors.navLinkActiveBorder} !important`,
    },
  },

  // Primary Buttons
  {
    selector: '.cta-button, .btn-primary, button[class*="primary"]',
    properties: {
      background: (colors) => `linear-gradient(135deg, ${colors.ctaPrimary} 0%, ${colors.ctaSecondary} 100%) !important`,
      color: 'white !important',
      border: 'none !important',
    },
  },
  {
    selector: '.cta-button:hover, .btn-primary:hover',
    properties: {
      'box-shadow': (colors) => `0 0 20px ${colors.ctaHoverShadow}50 !important`,
      transform: 'translateY(-1px)',
    },
  },

  // Secondary Buttons
  {
    selector: '.secondary-button, .btn-secondary, button[class*="secondary"]',
    properties: {
      border: (colors) => `1px solid ${colors.secondaryButtonBorder} !important`,
      color: (colors) => `${colors.secondaryButtonText} !important`,
      background: 'transparent !important',
    },
  },
  {
    selector: '.secondary-button:hover, .btn-secondary:hover',
    properties: {
      background: (colors) => `${colors.secondaryButtonHoverBg} !important`,
      color: (colors) => `${colors.secondaryButtonHoverText} !important`,
    },
  },

  // Explore Championships Button
  {
    selector: '.explore-championships-btn',
    properties: {
      border: (colors) => `1px solid ${colors.exploreChampionshipsBorder} !important`,
      color: (colors) => `${colors.exploreChampionshipsText} !important`,
      background: 'transparent !important',
      'background-color': 'transparent !important',
    },
  },
  {
    selector: '.explore-championships-btn:hover',
    properties: {
      background: (colors) => `linear-gradient(135deg, ${colors.exploreChampionshipsHoverBg}, ${colors.ctaSecondary}) !important`,
      'border-color': 'transparent !important',
      color: 'white !important',
      'box-shadow': (colors) => `0 0 30px ${colors.exploreChampionshipsHoverShadow}50 !important`,
    },
  },

  // Start Now Button
  {
    selector: '.start-now-btn',
    properties: {
      border: (colors) => `2px solid ${colors.startNowBorder} !important`,
      color: (colors) => `${colors.startNowText} !important`,
      background: 'transparent !important',
      'background-color': 'transparent !important',
    },
  },
  {
    selector: '.start-now-btn:hover',
    properties: {
      background: (colors) => `linear-gradient(135deg, ${colors.startNowHoverBg}, ${colors.ctaSecondary}) !important`,
      'border-color': 'transparent !important',
      color: 'white !important',
      'box-shadow': (colors) => `0 0 30px ${colors.startNowHoverShadow}50 !important`,
    },
  },

  // Text Classes
  {
    selector: '.text-primary, .primary-text',
    properties: {
      color: (colors) => `${colors.primaryText} !important`,
    },
  },
  {
    selector: '.text-secondary, .secondary-text',
    properties: {
      color: (colors) => `${colors.secondaryText} !important`,
    },
  },
  {
    selector: '.text-accent, .accent-text',
    properties: {
      color: (colors) => `${colors.accentText} !important`,
    },
  },

  // Gradient Text
  {
    selector: '.gradient-text, [class*="gradientText"]',
    properties: {
      background: (colors) => `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd}) !important`,
      '-webkit-background-clip': 'text !important',
      'background-clip': 'text !important',
      '-webkit-text-fill-color': 'transparent !important',
    },
  },

  // Championship Cards
  {
    selector: '.championship-card',
    properties: {
      'border-color': (colors) => `${colors.championshipCardBorder}50 !important`,
      background: (colors) => `${colors.championshipCardBg}0A !important`,
    },
  },
  {
    selector: '.championship-card:hover',
    properties: {
      'border-color': (colors) => `${colors.championshipCardHoverBorder}80 !important`,
      'box-shadow': (colors) => `0 0 25px ${colors.championshipCardHoverShadow}30 !important`,
    },
  },

  // Team Cards
  {
    selector: '.team-card',
    properties: {
      'border-color': (colors) => `${colors.teamCardBorder}40 !important`,
    },
  },
  {
    selector: '.team-card:hover',
    properties: {
      'border-color': (colors) => `${colors.teamCardHoverBorder}60 !important`,
      transform: 'translateY(-2px)',
    },
  },
  {
    selector: '.team-card .team-name',
    properties: {
      color: (colors) => `${colors.teamName} !important`,
    },
  },
  {
    selector: '.team-card .team-stats',
    properties: {
      color: (colors) => `${colors.teamStats} !important`,
    },
  },
  {
    selector: '.team-card .team-record',
    properties: {
      color: (colors) => `${colors.teamRecord} !important`,
    },
  },

  // Glass Cards
  {
    selector: '.glass-card',
    properties: {
      border: (colors) => `1px solid ${colors.glassCardBorder}30 !important`,
      background: (colors) => `${colors.glassCardBg}08 !important`,
    },
  },
  {
    selector: '.glass-card:hover',
    properties: {
      'border-color': (colors) => `${colors.glassCardHoverBorder}60 !important`,
      background: (colors) => `${colors.glassCardHoverBg}10 !important`,
    },
  },

  // Badges
  {
    selector: '.badge, .pill, [class*="Badge"]',
    properties: {
      background: (colors) => `${colors.badgeBg}15 !important`,
      color: (colors) => `${colors.badgeText} !important`,
      border: (colors) => `1px solid ${colors.badgeBorder}50 !important`,
    },
  },

  // Stats
  {
    selector: '.stat-value',
    properties: {
      color: (colors) => `${colors.statValue} !important`,
    },
  },
  {
    selector: '.stat-label',
    properties: {
      color: (colors) => `${colors.statLabel}80 !important`,
    },
  },
  {
    selector: '.metric-value',
    properties: {
      color: (colors) => `${colors.metricValue} !important`,
    },
  },

  // Tables
  {
    selector: 'th, .table-header',
    properties: {
      background: (colors) => `${colors.tableHeaderBg}10 !important`,
      color: (colors) => `${colors.tableHeaderText} !important`,
      'border-bottom': (colors) => `2px solid ${colors.tableHeaderBorder} !important`,
    },
  },
  {
    selector: 'tbody tr:hover',
    properties: {
      background: (colors) => `${colors.tableRowHoverBg}08 !important`,
    },
  },

  // Forms
  {
    selector: 'input:focus, textarea:focus, select:focus',
    properties: {
      'border-color': (colors) => `${colors.inputFocusBorder} !important`,
      'box-shadow': (colors) => `0 0 0 3px ${colors.inputFocusShadow}20 !important`,
      outline: 'none !important',
    },
  },

  // Loading
  {
    selector: '.loading-spinner',
    properties: {
      'border-color': (colors) => `${colors.loadingSpinnerBorder}30 !important`,
      'border-top-color': (colors) => `${colors.loadingSpinnerTop} !important`,
    },
  },

  // Links
  {
    selector: 'a:not(.nav-link):hover',
    properties: {
      color: (colors) => `${colors.linkHover} !important`,
    },
  },

  // Neon Text Effect
  {
    selector: '.neon-text',
    properties: {
      color: (colors) => `${colors.neonText} !important`,
      'text-shadow': (colors) => `0 0 10px ${colors.neonShadow}, 0 0 20px ${colors.neonShadow}, 0 0 30px ${colors.neonShadow} !important`,
    },
  },

  // Sport-Specific Elements
  {
    selector: '.football-element, .nfl-element, .basketball-element, .nba-element, .baseball-element, .mlb-element, .hockey-element, .nhl-element, .college-element',
    properties: {
      'border-color': (colors) => `${colors.sportElementBorder} !important`,
      color: (colors) => `${colors.sportElementText} !important`,
    },
  },
];

/**
 * Resolves color mappings from theme object
 */
const resolveColorMappings = (theme: SiteTheme, styleConfig: ColorMapping): SiteColors => {
  const resolvedColors: any = {};

  for (const [key, colorPath] of Object.entries(styleConfig)) {
    const [palette, shade] = colorPath.split('.');
    const paletteKey = palette as keyof SiteTheme;
    const themeSection = theme[paletteKey];

    // Type-safe access to color shades
    let color = '#6366f1'; // Default fallback
    if (themeSection && typeof themeSection === 'object' && shade in themeSection) {
      color = (themeSection as any)[shade];
    }

    resolvedColors[key] = color;
  }

  return resolvedColors as SiteColors;
};

/**
 * Generates CSS content from rules and resolved colors
 */
const generateCSSContent = (colors: SiteColors): string => {
  return CSS_RULES.map(rule => {
    const properties = Object.entries(rule.properties)
      .map(([prop, value]) => {
        const resolvedValue = typeof value === 'function' ? value(colors) : value;
        return `    ${prop}: ${resolvedValue};`;
      })
      .join('\n');

    return `  ${rule.selector} {\n${properties}\n  }`;
  }).join('\n\n');
};

/**
 * Main hook - Data-driven CSS generation for all 9 sites
 */
export const useDynamicCSS = () => {
  const siteConfig = useSiteConfig();

  useEffect(() => {
    // Early return if siteConfig is not available yet
    if (!siteConfig || !siteConfig.name || !siteConfig.theme) {
      console.log('Site config not yet available, skipping CSS injection');
      return;
    }

    try {
      // Remove existing site-specific styles
      const existingStyle = document.getElementById('site-css-variables');
      if (existingStyle) {
        existingStyle.remove();
      }

      // Get the style configuration for this site
      const styleConfig = SITE_STYLE_CONFIGS[siteConfig.name];

      if (!styleConfig) {
        console.warn(`No style configuration found for site: ${siteConfig.name}`);
        return;
      }

      // Resolve colors from theme using the style config
      const resolvedColors = resolveColorMappings(siteConfig.theme, styleConfig);

      // Generate CSS content using the rules template
      const cssContent = generateCSSContent(resolvedColors);

      // Create and inject the style element
      const styleElement = document.createElement('style');
      styleElement.id = 'site-css-variables';
      styleElement.innerHTML = cssContent;
      document.head.appendChild(styleElement);

      console.log(`✅ Applied data-driven CSS for ${siteConfig.name}`);
      console.log(`🎨 Using style config with ${Object.keys(styleConfig).length} color mappings`);
    } catch (error) {
      console.error('❌ Error injecting dynamic CSS:', error);
    }

    // Cleanup function
    return () => {
      const element = document.getElementById('site-css-variables');
      if (element) {
        element.remove();
      }
    };
  }, [siteConfig]);
};

/**
 * Legacy hooks maintained for backwards compatibility
 */
export const useDynamicCSSImport = useDynamicCSS;
export const useDynamicCSSVariables = useDynamicCSS;